// Frozen copy of main's code for the document_generation_v4_enabled / agency_brand_design_enabled flag-off path; delete with the flag.
import React from 'react';
import { motion } from 'motion/react';
import * as Headless from '@headlessui/react';
import { ChevronLeftIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { Badge } from '@oysterjs/ui/badge';
import { Spinner } from '@oysterjs/ui/spinner';

import {
  type CaraWorkspaceFieldsFragment,
  CaraWorkspaceState,
  type HtmlDocumentTemplateFieldsFragment
} from '~/api/graphql';
import { useArtifactEditor } from './use-artifact-editor';
import { useHtmlDocumentExportStyle } from '~/components/workspaces/hooks/use-html-document-export-style';
import { useWorkspaceAttachableArtifacts } from '~/components/workspaces/hooks/use-workspace-attachable-artifacts';
import { VisibilityPopover } from '~/components/visibility/visibility-popover';
import { useVisibilityEditAllowed } from '~/components/visibility/use-visibility-edit-allowed';

import { ArtifactVersionPicker } from '~/components/workspaces/artifact-focus-html-document-version-picker';
import { DocumentTemplateBadge } from '~/components/workspaces/artifact-focus-html-document-template-badge';
import { Editor } from './html-document-editor';
import { getArtifactHtmlDocumentTemplateId } from '~/components/workspaces/lib/html-document-artifact';
import { RenderArtifactButton } from '~/components/workspaces/artifact-focus-html-document-render-button';
import { SaveAsTemplateButton } from '~/components/workspaces/artifact-focus-html-document-save-as-template-button';
import {
  type SaveStatus,
  SaveStatusIndicator
} from '~/components/document-editor/artifact-focus-html-document-save-status-indicator';

export const HtmlDocumentFocus = (props: {
  workspace: CaraWorkspaceFieldsFragment;
  artifact: CaraWorkspaceFieldsFragment['artifacts'][0];
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[];
  onClose: () => void;
  onVersionChange: (artifact: CaraWorkspaceFieldsFragment['artifacts'][0]) => void;
  onConvertToTemplate?: () => void;
  isConvertingToTemplate?: boolean;
  autoOpenSaveDialog?: boolean;
  onSaveDialogClosed?: () => void;
  onSaveStatusChange?: (status: SaveStatus) => void;
}) => {
  const { artifact } = props;
  const { saveStatus, setSaveStatus, handleUpdate, handleTitleUpdate, handleVisibilityUpdate } =
    useArtifactEditor({
      workspaceId: props.workspace.id,
      artifactId: artifact.id,
      version: artifact.version
    });
  const visibilityEditAllowed = useVisibilityEditAllowed(props.workspace.agencyUser?.id);

  const onSaveStatusChange = props.onSaveStatusChange;
  React.useEffect(() => {
    onSaveStatusChange?.(saveStatus);
  }, [saveStatus, onSaveStatusChange]);

  const [showPreview, setShowPreview] = React.useState(false);
  React.useEffect(() => {
    setShowPreview(false);
  }, [artifact.id]);

  // Inline title editing: local value synced from artifact
  const [titleValue, setTitleValue] = React.useState(artifact.document?.title ?? '');
  React.useEffect(() => {
    setTitleValue(artifact.document?.title ?? '');
  }, [artifact.id, artifact.version, artifact.document?.title]);

  const versionsOfCurrentArtifact = React.useMemo(
    () =>
      props.workspace.artifacts
        .filter((a) => a.id === artifact.id)
        .sort((a, b) => b.version - a.version),
    [props.workspace.artifacts, artifact.id]
  );

  const { workspaceFiles, workspaceMessageFiles, workspaceArtifacts } =
    useWorkspaceAttachableArtifacts(props.workspace);

  const { selectedStyleId, setSelectedStyleId, previewStyleId } =
    useHtmlDocumentExportStyle(artifact);

  const htmlDocumentTemplateID = getArtifactHtmlDocumentTemplateId(artifact);
  const templateLabel = htmlDocumentTemplateID
    ? props.htmlDocumentTemplates.find((t) => t.id === htmlDocumentTemplateID)?.title
    : null;

  return (
    <motion.div className="h-full flex flex-col">
      <div className="flex flex-row items-center gap-2">
        <Headless.Button
          className="rounded-full -ml-3 py-1.5 pl-2 pr-3 flex items-center justify-center gap-2 hover:bg-neutral-200/65 dark:hover:bg-white/10 text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white transition-all duration-150 cursor-pointer"
          onClick={props.onClose}
        >
          <ChevronLeftIcon className="size-4" />
          <div className="text-xs">Close editor</div>
        </Headless.Button>
      </div>
      {/* Row 1: title, Save as template, and Open PDF (split) */}
      <div className="mt-0.5 flex flex-row items-center justify-between gap-4 min-w-0">
        <input
          value={titleValue}
          onChange={(e) => setTitleValue(e.target.value)}
          onBlur={() => handleTitleUpdate(titleValue)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          className="min-w-0 flex-1 bg-transparent border-none outline-none text-lg leading-tight font-medium text-neutral-900 dark:text-white truncate placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:ring-0 p-0"
          placeholder="Untitled"
          aria-label="Document title"
        />
        <div className="flex items-center gap-2 shrink-0">
          <SaveAsTemplateButton
            artifactContent={artifact.document?.content || ''}
            artifactTitle={artifact.document?.title}
            onConvertRequest={props.onConvertToTemplate}
            externalOpen={props.autoOpenSaveDialog}
            externalInitialStep={2}
            externalInitialMethod="convert"
            onExternalClose={props.onSaveDialogClosed}
            artifactWasConverted={!!artifact.document?.sourceFileId}
          />
          <RenderArtifactButton
            artifact={artifact}
            workspaceId={props.workspace.id}
            htmlDocumentTemplates={props.htmlDocumentTemplates}
            workspaceArtifacts={workspaceArtifacts}
            workspaceFiles={workspaceFiles}
            workspaceMessageFiles={workspaceMessageFiles}
            selectedStyleId={selectedStyleId}
            onSelectedStyleIdChange={setSelectedStyleId}
          />
          {visibilityEditAllowed && (
            <VisibilityPopover visibility={artifact.visibility} onSave={handleVisibilityUpdate} />
          )}
        </div>
      </div>
      {/* Row 2: version + provenance on left; save status */}
      <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <ArtifactVersionPicker
            versions={versionsOfCurrentArtifact}
            allArtifacts={props.workspace.artifacts}
            currentArtifact={artifact}
            onVersionChange={props.onVersionChange}
          />
          {!!htmlDocumentTemplateID && !!templateLabel && (
            <DocumentTemplateBadge
              htmlDocumentTemplateId={htmlDocumentTemplateID}
              templateTitle={templateLabel}
            />
          )}
          {!!artifact.editedAt && (
            <Badge color="neutral">
              <PencilSquareIcon className="size-3" />
              Edited
            </Badge>
          )}
          {props.workspace.state !== CaraWorkspaceState.Working && (
            <SaveStatusIndicator status={saveStatus} />
          )}
        </div>
      </div>
      {props.isConvertingToTemplate && <TemplateProgressBanner />}
      <Editor
        key={`${artifact.id}-${artifact.version}`}
        content={artifact.document?.content || ''}
        editable={props.workspace.state !== CaraWorkspaceState.Working}
        onUpdate={handleUpdate}
        onSaveStatusChange={setSaveStatus}
        workspaceId={props.workspace.id}
        artifactId={artifact.id}
        artifactVersion={artifact.version}
        templateId={previewStyleId}
        showPreview={showPreview}
        onPreviewToggle={() => setShowPreview(!showPreview)}
      />
    </motion.div>
  );
};

const TemplateProgressBanner = () => (
  <div className="mt-2 rounded-lg bg-sky-50 dark:bg-sky-500/10 border border-sky-200 dark:border-sky-500/30 px-3 py-2">
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm font-medium text-neutral-900 dark:text-white">
        Building template
      </span>
      <Spinner size="size-3.5" color="sky" />
    </div>
    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
      Cara is replacing variable content with placeholders…
    </p>
  </div>
);
